// ═══════════════════════════════════════════════════════════════════════════════
// Módulo: Financiamento de Projetos — Serviço de Dados centralizado
// ═══════════════════════════════════════════════════════════════════════════════

import { supabase } from '@/integrations/supabase/client';

// ── Chunking helper for .in() queries ────────────────────────────────────────

const CHUNK_SIZE = 50;

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

async function queryInChunks<T>(
  ids: string[],
  queryFn: (chunk: string[]) => Promise<T[]>,
): Promise<T[]> {
  if (ids.length === 0) return [];
  const chunks = chunkArray(ids, CHUNK_SIZE);
  const results = await Promise.all(chunks.map(queryFn));
  return results.flat();
}

// ── Financiamentos ───────────────────────────────────────────────────────────

export async function fetchFinanciamentosAtivos() {
  const { data, error } = await (supabase as any)
    .from('financiamento_contratos')
    .select('*')
    .in('status', ['ativo', 'provisorio']);
  if (error) throw error;
  return data || [];
}

export async function fetchAllFinanciamentos() {
  const { data, error } = await (supabase as any)
    .from('financiamento_contratos')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ── Contratos (emp_contratos) — leitura ──────────────────────────────────────

export async function fetchContratosByIds(ids: string[]) {
  return queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_contratos')
      .select('id, nome, contratada, cluster_id, valor_global, data_inicio, data_fim_prevista, status')
      .in('id', chunk);
    if (error) throw error;
    return data || [];
  });
}

export async function fetchContratosDisponiveis() {
  const { data, error } = await (supabase as any)
    .from('emp_contratos')
    .select('id, nome, contratada, status')
    .order('nome');
  if (error) throw error;
  return data || [];
}

// ── Progresso físico — leitura ───────────────────────────────────────────────

export async function fetchProgressoByContratoIds(ids: string[]) {
  return queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_vw_progresso_fisico_contrato')
      .select('*')
      .in('contrato_id', chunk);
    if (error) throw error;
    return data || [];
  });
}

// ── Risco — leitura ──────────────────────────────────────────────────────────

export async function fetchRiscoByContratoIds(ids: string[]) {
  return queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('vw_risk_contract_ranking')
      .select('contract_id, score_risco, nivel_criticidade, tendencia, prob_atraso_mc')
      .in('contract_id', chunk);
    if (error) throw error;
    return data || [];
  });
}

// ── Boletins — leitura ───────────────────────────────────────────────────────

export async function fetchBoletinsAprovadosByContratoIds(ids: string[]) {
  return queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_boletins_medicao')
      .select('contrato_id, numero_boletim, valor_liquido, periodo_fim, status')
      .in('contrato_id', chunk)
      .eq('status', 'aprovado_para_faturamento')
      .order('numero_boletim', { ascending: false });
    if (error) throw error;
    return data || [];
  });
}

// ── Aditivos — leitura ──────────────────────────────────────────────────────

export async function fetchAditivosAprovadosByContratoIds(ids: string[]) {
  return queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_aditivos')
      .select('contrato_id, tipo, valor_aditivo, status')
      .in('contrato_id', chunk)
      .eq('status', 'aprovado');
    if (error) throw error;
    return data || [];
  });
}

// ── Instituições ─────────────────────────────────────────────────────────────

export async function fetchInstituicoes() {
  const { data, error } = await (supabase as any)
    .from('if_instituicoes')
    .select('*')
    .order('nome');
  if (error) throw error;
  return data || [];
}

export async function createInstituicao(input: { nome: string; cnpj?: string; contato_nome?: string; contato_email?: string }) {
  const { error } = await (supabase as any)
    .from('if_instituicoes')
    .insert({
      nome: input.nome,
      cnpj: input.cnpj || null,
      contato_nome: input.contato_nome || null,
      contato_email: input.contato_email || null,
    });
  if (error) throw error;
}

// ── Vínculos de Financiamento ────────────────────────────────────────────────

export async function createFinanciamento(input: {
  contrato_id: string;
  instituicao_id: string;
  tipo_vinculo: 'monitoramento' | 'financiamento';
  data_inicio_financiamento: string | null;
}) {
  const { error } = await (supabase as any)
    .from('financiamento_contratos')
    .insert({
      contrato_id: input.contrato_id,
      instituicao_id: input.instituicao_id,
      tipo_vinculo: input.tipo_vinculo,
      data_inicio_financiamento: input.data_inicio_financiamento,
      status: 'ativo',
    });
  if (error) throw error;
}

export async function updateFinanciamentoStatus(id: string, status: string) {
  const { error } = await (supabase as any)
    .from('financiamento_contratos')
    .update({ status })
    .eq('id', id);
  if (error) throw error;
}

// ── Convites ─────────────────────────────────────────────────────────────────

export async function fetchConvites(contratoId?: string) {
  let query = (supabase as any)
    .from('if_convites')
    .select('*')
    .order('created_at', { ascending: false });
  if (contratoId) query = query.eq('contrato_id', contratoId);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function createConvite(input: {
  contrato_id: string;
  tipo_acesso: 'monitoramento' | 'financiamento';
  instituicao_destino_id?: string | null;
}) {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await (supabase as any)
    .from('if_convites')
    .insert({
      contrato_id: input.contrato_id,
      tipo_acesso: input.tipo_acesso,
      criado_por: userData?.user?.id,
      instituicao_destino_id: input.instituicao_destino_id || null,
    })
    .select('token')
    .single();
  if (error) throw error;
  return data;
}

export async function resgatarConvite(token: string) {
  // Fetch the invite
  const { data: convite, error: fetchErr } = await (supabase as any)
    .from('if_convites')
    .select('*')
    .eq('token', token)
    .eq('status', 'pendente')
    .gt('expires_at', new Date().toISOString())
    .single();
  if (fetchErr || !convite) throw new Error('Convite inválido, expirado ou já utilizado.');

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) throw new Error('Usuário não autenticado.');

  // Get user's institution — or auto-provision for global admins
  let instituicaoId: string;

  const { data: instUser } = await (supabase as any)
    .from('if_instituicao_usuarios')
    .select('instituicao_id')
    .eq('user_id', userId)
    .eq('ativo', true)
    .maybeSingle();

  if (instUser) {
    instituicaoId = instUser.instituicao_id;
  } else if (convite.instituicao_destino_id) {
    // Use the institution specified in the invite
    instituicaoId = convite.instituicao_destino_id;
    // Bind user to that institution
    await (supabase as any)
      .from('if_instituicao_usuarios')
      .insert({ user_id: userId, instituicao_id: instituicaoId, cargo: 'admin', ativo: true });
  } else {
    // Auto-provision: check if user is admin/coadmin via user_roles
    const { data: adminRole } = await (supabase as any)
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .in('role', ['admin', 'coadmin'])
      .maybeSingle();

    if (!adminRole) {
      throw new Error('Usuário não está vinculado a nenhuma instituição. Solicite um convite com instituição de destino.');
    }

    // Auto-create institution for admin
    const { data: profile } = await (supabase as any)
      .from('profiles')
      .select('name, email')
      .eq('id', userId)
      .single();

    const instName = `Administração Global — ${profile?.name || 'Admin'}`;

    const { data: newInst, error: instErr } = await (supabase as any)
      .from('if_instituicoes')
      .insert({ nome: instName, cnpj: null, ativo: true, created_by: userId })
      .select('id')
      .single();
    if (instErr) throw instErr;

    instituicaoId = newInst.id;

    // Bind admin to institution
    await (supabase as any)
      .from('if_instituicao_usuarios')
      .insert({ user_id: userId, instituicao_id: instituicaoId, cargo: 'admin', ativo: true });
  }

  // Accept invite
  const { error: updateErr } = await (supabase as any)
    .from('if_convites')
    .update({ status: 'aceito', aceito_por: userId, aceito_em: new Date().toISOString() })
    .eq('id', convite.id);
  if (updateErr) throw updateErr;

  // Create financiamento link
  const { error: createErr } = await (supabase as any)
    .from('financiamento_contratos')
    .insert({
      contrato_id: convite.contrato_id,
      instituicao_id: instituicaoId,
      tipo_vinculo: convite.tipo_acesso,
      status: 'provisorio',
      expires_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    });
  if (createErr) throw createErr;
}

export async function revogarConvite(conviteId: string) {
  const { error } = await (supabase as any)
    .from('if_convites')
    .update({ status: 'revogado' })
    .eq('id', conviteId);
  if (error) throw error;
}

export async function fetchConviteByToken(token: string) {
  const { data, error } = await (supabase as any)
    .from('if_convites')
    .select('id, contrato_id, tipo_acesso, expires_at, status, instituicao_destino_id')
    .eq('token', token)
    .single();
  if (error) throw error;
  return data;
}

export async function fetchContratoPreview(contratoId: string) {
  const { data, error } = await (supabase as any)
    .from('emp_contratos')
    .select('id, status, cluster_id')
    .eq('id', contratoId)
    .single();
  if (error) throw error;

  // Get cluster name
  let clusterNome = '—';
  if (data?.cluster_id) {
    const { data: cl } = await (supabase as any)
      .from('clusters')
      .select('name')
      .eq('id', data.cluster_id)
      .single();
    clusterNome = cl?.name || '—';
  }

  // Get progress
  const { data: prog } = await (supabase as any)
    .from('emp_vw_progresso_fisico_contrato')
    .select('progresso_fisico_pct')
    .eq('contrato_id', contratoId)
    .maybeSingle();

  // Get risk
  const { data: risk } = await (supabase as any)
    .from('vw_risk_contract_ranking')
    .select('nivel_criticidade, tendencia')
    .eq('contract_id', contratoId)
    .maybeSingle();

  const progresso = prog?.progresso_fisico_pct ?? 0;
  let estagio = 'Início';
  if (progresso >= 75) estagio = 'Conclusão (75-100%)';
  else if (progresso >= 50) estagio = 'Avançado (50-75%)';
  else if (progresso >= 25) estagio = 'Desenvolvimento (25-50%)';
  else estagio = 'Início (0-25%)';

  return {
    estagio_execucao: estagio,
    classificacao_risco: risk?.nivel_criticidade || 'Sem dados',
    tendencia: risk?.tendencia || null,
    cluster_responsavel: clusterNome,
  };
}

// ── Governança de Vínculos ───────────────────────────────────────────────────

export async function confirmarVinculo(vinculoId: string) {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await (supabase as any)
    .from('financiamento_contratos')
    .update({
      status: 'ativo',
      aprovado_por: userData?.user?.id,
      aprovado_em: new Date().toISOString(),
      expires_at: null,
    })
    .eq('id', vinculoId);
  if (error) throw error;
}

export async function suspenderVinculo(vinculoId: string) {
  const { error } = await (supabase as any)
    .from('financiamento_contratos')
    .update({ status: 'suspenso' })
    .eq('id', vinculoId);
  if (error) throw error;
}

export async function encerrarVinculo(vinculoId: string, motivo: string) {
  const { error } = await (supabase as any)
    .from('financiamento_contratos')
    .update({ status: 'encerrado', motivo_encerramento: motivo })
    .eq('id', vinculoId);
  if (error) throw error;
}

export async function fetchMeusContratos() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user?.id) return [];

  // Get user's institution
  const { data: instUser } = await (supabase as any)
    .from('if_instituicao_usuarios')
    .select('instituicao_id')
    .eq('user_id', userData.user.id)
    .eq('ativo', true)
    .single();
  if (!instUser) return [];

  // Get all financiamentos for this institution
  const { data, error } = await (supabase as any)
    .from('financiamento_contratos')
    .select('*')
    .eq('instituicao_id', instUser.instituicao_id)
    .in('status', ['ativo', 'provisorio'])
    .order('created_at', { ascending: false });
  if (error) throw error;

  // Enrich with contract names
  const ids = (data || []).map((d: any) => d.contrato_id);
  if (ids.length === 0) return [];

  const { data: contratos } = await (supabase as any)
    .from('emp_contratos')
    .select('id, nome, status')
    .in('id', ids);

  const nameMap = new Map((contratos || []).map((c: any) => [c.id, c]));

  return (data || []).map((d: any) => {
    const c = nameMap.get(d.contrato_id) as any;
    return {
      ...d,
      contrato_nome: c?.nome || '—',
      contrato_status: c?.status || '—',
    };
  });
}

// ── Alertas ──────────────────────────────────────────────────────────────────

export async function fetchAlertasAtivos(contratoId?: string) {
  let query = (supabase as any)
    .from('if_alertas')
    .select('*')
    .eq('status', 'ativo')
    .order('created_at', { ascending: false })
    .limit(50);
  if (contratoId) query = query.eq('contrato_id', contratoId);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function fetchAlertasCount() {
  const { count, error } = await (supabase as any)
    .from('if_alertas')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'ativo');
  if (error) throw error;
  return count || 0;
}

export async function marcarAlertaLido(alertaId: string) {
  const { error } = await (supabase as any)
    .from('if_alertas')
    .update({ status: 'lido', lido_em: new Date().toISOString() })
    .eq('id', alertaId);
  if (error) throw error;
}

// ── Risco Histórico ──────────────────────────────────────────────────────────

export async function fetchRiskHistorico(contratoId: string, dias = 90) {
  const desde = new Date();
  desde.setDate(desde.getDate() - dias);
  const { data, error } = await (supabase as any)
    .from('if_risk_historico')
    .select('*')
    .eq('contrato_id', contratoId)
    .gte('data_referencia', desde.toISOString().split('T')[0])
    .order('data_referencia', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchRiskHistoricoByContratoIds(contratoIds: string[]) {
  return queryInChunks(contratoIds, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('if_risk_historico')
      .select('contrato_id, score_composto, prob_atraso, data_referencia')
      .in('contrato_id', chunk)
      .order('data_referencia', { ascending: true });
    if (error) throw error;
    return data || [];
  });
}

// ── Health Check ─────────────────────────────────────────────────────────────

export async function fetchHealthCheck() {
  const { data, error } = await (supabase as any).rpc('if_check_snapshot_health');
  if (error) throw error;
  return data?.[0] || data;
}

// ── Saldo Contratual ─────────────────────────────────────────────────────────

export async function fetchVersaoVigente(contratoId: string) {
  const { data, error } = await (supabase as any)
    .from('emp_planilha_versoes')
    .select('id')
    .eq('contrato_id', contratoId)
    .eq('status', 'vigente')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.id as string | null;
}

export async function fetchSaldoByVersao(versaoId: string) {
  const { data, error } = await supabase
    .from('emp_vw_saldo_medicao' as any)
    .select('*')
    .eq('versao_id', versaoId);
  if (error) throw error;
  return data || [];
}

// ── Alertas por contrato (para inteligência) ─────────────────────────────────

export async function fetchAlertasPorContrato() {
  const { data, error } = await (supabase as any)
    .from('if_alertas')
    .select('contrato_id')
    .eq('status', 'ativo');
  if (error) throw error;
  return data || [];
}

// ── Portfólio Agregado ───────────────────────────────────────────────────────

export async function fetchPortfolioAgregado() {
  const { data, error } = await (supabase as any)
    .from('vw_portfolio_agregado')
    .select('*');
  if (error) throw error;
  return data || [];
}

// ── Sparkline Data (S-Curve miniature) ───────────────────────────────────────

export interface SparklinePoint {
  data: string;
  valor: number;
}

export interface ContratoSparklineData {
  contrato_id: string;
  points: SparklinePoint[];
}

const SPARKLINE_MAX_POINTS = 30;

function downsample(points: SparklinePoint[], maxPoints: number): SparklinePoint[] {
  if (points.length <= maxPoints) return points;
  const step = (points.length - 1) / (maxPoints - 1);
  const result: SparklinePoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    result.push(points[Math.round(i * step)]);
  }
  return result;
}

export async function fetchSparklineDataByContratoIds(ids: string[]): Promise<ContratoSparklineData[]> {
  if (ids.length === 0) return [];
  const allRows = await queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_vw_curva_s_fisica_contrato')
      .select('contrato_id, data, curva_realizada')
      .in('contrato_id', chunk)
      .not('curva_realizada', 'is', null)
      .order('data', { ascending: true });
    if (error) throw error;
    return data || [];
  });

  // Group by contrato and downsample
  const grouped = new Map<string, SparklinePoint[]>();
  for (const r of allRows as any[]) {
    const cid = r.contrato_id;
    if (!grouped.has(cid)) grouped.set(cid, []);
    grouped.get(cid)!.push({ data: r.data, valor: Number(r.curva_realizada) || 0 });
  }

  return Array.from(grouped.entries()).map(([contrato_id, points]) => ({
    contrato_id,
    points: downsample(points, SPARKLINE_MAX_POINTS),
  }));
}

// ── Execuções Não Medidas (validadas mas sem boletim) ────────────────────────

export interface ExecucaoNaoMedidaResumo {
  contrato_id: string;
  total_registros: number;
  valor_estimado: number;
}

export async function fetchExecucoesNaoMedidasByContratoIds(ids: string[]): Promise<ExecucaoNaoMedidaResumo[]> {
  if (ids.length === 0) return [];
  return queryInChunks(ids, async (chunk) => {
    // Fetch execuções without boletim
    const { data: execs, error } = await supabase
      .from('emp_validacoes_execucao')
      .select('contrato_id, quantidade_executada, valor_unitario_snapshot, planilha_item_id')
      .in('contrato_id', chunk)
      .is('boletim_id', null)
      .in('status', ['vinculada_a_boletim', 'validado', 'aprovado']);
    if (error) throw error;
    if (!execs || execs.length === 0) return [];

    // Collect items needing fallback (valor_unitario_snapshot = 0 or null)
    const needsFallback = execs.filter((e: any) => !e.valor_unitario_snapshot || Number(e.valor_unitario_snapshot) === 0);
    let fallbackMap = new Map<string, number>();
    if (needsFallback.length > 0) {
      const itemIds = [...new Set(needsFallback.map((e: any) => e.planilha_item_id).filter(Boolean))];
      if (itemIds.length > 0) {
        const { data: items } = await supabase
          .from('emp_planilha_itens')
          .select('id, preco_unitario')
          .in('id', itemIds);
        (items || []).forEach((i: any) => fallbackMap.set(i.id, Number(i.preco_unitario) || 0));
      }
    }

    // Aggregate per contrato
    const aggMap = new Map<string, { count: number; valor: number }>();
    for (const e of execs as any[]) {
      const cid = e.contrato_id;
      let vu = Number(e.valor_unitario_snapshot) || 0;
      if (vu === 0 && e.planilha_item_id) {
        vu = fallbackMap.get(e.planilha_item_id) || 0;
      }
      const val = (Number(e.quantidade_executada) || 0) * vu;
      const agg = aggMap.get(cid) || { count: 0, valor: 0 };
      agg.count += 1;
      agg.valor += val;
      aggMap.set(cid, agg);
    }

    return Array.from(aggMap.entries()).map(([contrato_id, v]) => ({
      contrato_id,
      total_registros: v.count,
      valor_estimado: v.valor,
    }));
  });
}

export async function fetchExecucoesNaoMedidasByContrato(contratoId: string): Promise<ExecucaoNaoMedidaResumo | null> {
  const results = await fetchExecucoesNaoMedidasByContratoIds([contratoId]);
  return results.find(r => r.contrato_id === contratoId) || null;
}

// ── Boletins por Status ──────────────────────────────────────────────────────

export interface BoletimStatusResumo {
  status: string;
  total_boletins: number;
  valor_bruto: number;
  valor_liquido: number;
  valor_glosas: number;
}

export async function fetchBoletinsPorStatusByContratoIds(ids: string[]): Promise<{ contrato_id: string; resumos: BoletimStatusResumo[] }[]> {
  if (ids.length === 0) return [];
  const allBoletins = await queryInChunks(ids, async (chunk) => {
    const { data, error } = await (supabase as any)
      .from('emp_boletins_medicao')
      .select('contrato_id, status, valor_bruto, valor_liquido, valor_glosas')
      .in('contrato_id', chunk);
    if (error) throw error;
    return data || [];
  });

  // Group by contrato → status
  const grouped = new Map<string, Map<string, BoletimStatusResumo>>();
  for (const b of allBoletins as any[]) {
    const cid = b.contrato_id;
    if (!grouped.has(cid)) grouped.set(cid, new Map());
    const statusMap = grouped.get(cid)!;
    const s = b.status || 'desconhecido';
    const agg = statusMap.get(s) || { status: s, total_boletins: 0, valor_bruto: 0, valor_liquido: 0, valor_glosas: 0 };
    agg.total_boletins += 1;
    agg.valor_bruto += Number(b.valor_bruto) || 0;
    agg.valor_liquido += Number(b.valor_liquido) || 0;
    agg.valor_glosas += Number(b.valor_glosas) || 0;
    statusMap.set(s, agg);
  }

  return Array.from(grouped.entries()).map(([contrato_id, statusMap]) => ({
    contrato_id,
    resumos: Array.from(statusMap.values()),
  }));
}

export async function fetchBoletinsPorStatusByContrato(contratoId: string): Promise<BoletimStatusResumo[]> {
  const results = await fetchBoletinsPorStatusByContratoIds([contratoId]);
  return results.find(r => r.contrato_id === contratoId)?.resumos || [];
}
